import clsx from "clsx";
import React, {
  ReactElement,
  ReactNode,
  useEffect,
  useState,
  useTransition,
} from "react";
import { MdExpandMore } from "react-icons/md";
import { ICON_SIZES } from "../../helpers/constants";
import Badge from "../Badge/Badge";
import { motion } from "framer-motion";
import { IButton } from "../Button/Button";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import SlideDownUpAnimation from "../Animations/SlideDownUpAnimation";

type CollapsibleBlockType = {
  title: string;
  children: ReactElement;
  open?: boolean;
  badge?: string;
  uppercase?: boolean;
  buttons?: Array<IButton>;
  onChange?: (isOpen: boolean) => void;
  icon?: ReactNode;
  badgeKind?: "danger" | "info" | "success" | "warning";
  blocked?: boolean;
  keepAlive?: boolean;
};

const CollapsibleBlock: React.FC<CollapsibleBlockType> = ({
  title,
  children,
  open = true,
  badge = null,
  uppercase = false,
  buttons = [],
  icon,
  onChange = null,
  badgeKind = "info",
  blocked = false,
  keepAlive = false,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(open);
  const [isPending, startTransition] = useTransition();
  const [showButtons, setShowButtons] = useState<boolean>(false);
  const [shakeBadge, setShakeBadge] = useState<boolean>(false);
  const [floatingIcon, setFloatingIcon] = useState<boolean>(false);

  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  useEffect(() => {
    setShowButtons(isOpen);
  }, [isOpen]);

  return (
    <div
      className={clsx([
        "flex",
        "flex-col",
        "w-full",
        {
          "gap-4": true,
        },
      ])}
    >
      <motion.div
        className={clsx([
          "flex",
          "flex-row",
          "justify-between",
          "items-center",
          {
            "cursor-pointer": !blocked,
            "border-gray-200 dark:border-gray-600": !isOpen,
            "border-blue-200 dark:border-cyan-800": isOpen,
          },
          "border-b",
          "border-dashed",
          "pb-1",
        ])}
        onClick={() => {
          if (blocked) return false;
          if (onChange) {
            onChange?.(!isOpen);
          } else {
            setIsOpen(!isOpen);
          }
        }}
        whileHover={blocked ? {} : { opacity: 0.7 }}
        whileTap={blocked ? {} : { opacity: 0.7 }}
        onHoverStart={() => {
          if (blocked) return false;
          // setShakeBadge(true);
          setFloatingIcon(true);
        }}
        onHoverEnd={() => {
          if (blocked) return false;

          setShakeBadge(false);
          setFloatingIcon(false);
        }}
      >
        <motion.div
          className={clsx([
            "flex",
            "flex-shrink-0",
            "flex-grow",
            "font-medium",
            "text-sm",
            "md:text-base",
            "flex-row",
            "gap-1",
            "items-center",
            {
              "text-blue-400 dark:text-cyan-400": isOpen,
            },
          ])}
        >
          {icon && icon}
          {title}
        </motion.div>

        <div className={clsx(["flex", "items-center", "flex-row"])}>
          {badge && (
            <motion.div
              key={shakeBadge ? "shaking" : "idle"}
              animate={shakeBadge ? { x: [0, -2, 2, -2, 2, 0] } : {}}
              transition={{
                duration: 0.4,
                ease: "easeInOut",
              }}
            >
              <Badge kind={badgeKind} text={badge} />
            </motion.div>
          )}
          {!blocked && (
            <motion.div
              initial={{
                rotate: isOpen ? 180 : 0,
              }}
              animate={{
                rotate: isOpen ? 180 : 0,
                y: floatingIcon ? [0, -1.5, 1.5, -1.5, 1.5, 0] : 0,
              }}
              transition={{
                rotate: { duration: 0.2 },
                y: floatingIcon
                  ? { duration: 1.2, repeat: Infinity, ease: "easeInOut" }
                  : {},
              }}
            >
              <MdExpandMore
                size={ICON_SIZES.MD}
                className={clsx([
                  {
                    "text-red-400 dark:text-purple-400": isOpen,
                    "opacity-50": !isOpen,
                  },
                ])}
              />
            </motion.div>
          )}
        </div>
      </motion.div>

      <SlideDownUpAnimation show={isOpen} keepAlive={keepAlive}>
        {children}
      </SlideDownUpAnimation>

      {!!(buttons?.length && showButtons) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="flex w-full"
        >
          <ButtonGroup buttons={buttons} align="right" gap={1} fullWidth />
        </motion.div>
      )}
    </div>
  );
};

export default CollapsibleBlock;
